import { EntityManagement, getErrorFormat } from '@genesislcap/foundation-entity-management';
import { styles } from './cascade-user.styles';
import { template } from './cascade-user.template';
import { attr, customElement, observable } from '@microsoft/fast-element';
import { Auth } from '@genesislcap/foundation-comms';
import { GridProRendererTypes } from '@genesislcap/grid-pro';
import { notifyError } from '../../../utils/notify';
import { IUserService } from '../../../services/user.service';
import { ICompanyService } from '../../../services/company.service';

const name = 'cascade-user-management';

//Platform provided variable in user entity management
const defaultColumnConfig = {
  enableCellChangeFlash: true,
  enableRowGroup: false,
  enablePivot: true,
  enableValue: true,
};

/**
 * Defines the default columns which are present on the user management component
 *
 * @remarks
 *
 * This is exported so you can override and mix-in custom columns along with the default
 * @public
 */
export const UsersColumnConfig = [
  {
    ...defaultColumnConfig,
    field: 'USER_NAME',
    headerName: 'Username',
  },
  {
    ...defaultColumnConfig,
    field: 'FIRST_NAME',
    headerName: 'First Name',
  },
  {
    ...defaultColumnConfig,
    field: 'LAST_NAME',
    headerName: 'Last Name',
  },
  {
    ...defaultColumnConfig,
    field: 'EMAIL_ADDRESS',
    headerName: 'Email',
  },
  {
    ...defaultColumnConfig,
    field: 'LAST_LOGIN',
    headerName: 'Last Login',
    valueFormatter: ({ value }) =>
      value
        ? new Intl.DateTimeFormat('default', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          }).format(value)
        : '',
  },
];

@customElement({
  name,
  template,
  styles,
})
export class CascadeUserManagement extends EntityManagement {
  //This grid is a custom version of the grid for the user entity management
  //There is a possibility that this modal could differ from the platform one in the future
  //This file is not much different from the platform one. This custom object was needed for changing the template to use the custom modal

  @Auth private auth: Auth;

  @observable columns: any = [...UsersColumnConfig, this.statusColumn()];

  @attr resourceName = 'ALL_USERS';
  @attr title = 'User Management';
  @attr entityLabel = 'User';
  @attr entityID: string = 'COUNTERPARTY_ID';

  @observable gridOptions = {
    scrollbarWidth: 10,
  };
  @IUserService userService: IUserService;
  @ICompanyService companyService: ICompanyService;

  //Platform provided function in user entity management
  constructor() {
    super();
         const isAdmin = (this.userService.hasAdminEditUserRights()
            || this.userService.hasCascadeAdminRights());
         this.createEvent = this.auth.currentUser.hasPermission('INSERT_USER') && isAdmin
           ? 'EVENT_INSERT_USER'
           : '';
         this.deleteEvent = this.auth.currentUser.hasPermission('DELETE_USER') && isAdmin
           ? 'EVENT_DELETE_USER'
           : '';
         this.updateEvent = this.auth.currentUser.hasPermission('AMEND_USER') && isAdmin ? 'EVENT_AMEND_USER' : '';
  }

  //Platform provided function in user entity management
  statusRendererSelector(params) {
    return {
      component: GridProRendererTypes.action,
      params: {
        actionClick: () => this.changeStatus(params),
        actionName: params.value === 'ENABLED' ? 'Deactivate' : 'Activate',
        appearance: params.value === 'ENABLED' ? 'secondary-orange': 'primary',
      },
    };
  }

  //Platform provided function in user entity management
  public statusColumn() {
    return {
      ...defaultColumnConfig,
      headerName: 'Status',
      field: 'STATUS',
      minWidth: 100,
      width: 100,
      suppressSizeToFit: true,
      cellRendererSelector: this.statusRendererSelector.bind(this),
    };
  }

  //Platform provided function in user entity management
  async changeStatus(params) {
    const isEnabled = params.data.STATUS === 'ENABLED';

    // optimistically set value and store previous value
    const previousValue = params.data.STATUS;
    params.setValue(isEnabled ? 'DISABLED' : 'ENABLED');

    const changeStatusRequest = await this.connect.commitEvent(
      isEnabled ? 'EVENT_DISABLE_USER' : 'EVENT_ENABLE_USER',
      {
        DETAILS: {
          USER_NAME: params.data.USER_NAME,
        },
      },
    );

    if (changeStatusRequest.ERROR) {
      changeStatusRequest.ERROR.forEach((err) => {
        notifyError('', getErrorFormat(err));
      });
      // bring back original value
      params.setValue(previousValue);
    }
  }
}
